// [2026-09-04] ADDED — the revamped customer shell: product rail + top navigation.
//
// Built to the IncProctor Figma (page "Final"). It REPLACES the 236px left sidebar that
// PlatformApp used to draw inline; that markup is kept, commented out and date-stamped, at the
// bottom of PlatformApp.jsx so the previous shell can be restored in one edit.
//
// WHAT THIS FILE IS AND IS NOT
// ----------------------------
// It is CHROME ONLY. It holds no session logic, makes no API call and owns no screen state: it
// takes the current view and a setter and renders navigation around `children`. That is deliberate
// — the sign-in gate, the /me bootstrap and the 401 handling all stay in PlatformApp, where they
// already worked, so this revamp cannot change who can see what.
//
// IT DOES NOT TOUCH App.jsx. The old shell exports `NavGroup` and `NavItem`, and
// proctoring-frontend-admin imports both to build its staff sidebar (src/index.js:51 calls that a
// cross-repo contract). Nothing here imports or modifies them, so the admin app is unaffected by
// every line of this file.
//
// THE FIVE-ITEM NAV IS THE FIGMA'S, AND THIS PRODUCT HAS SEVEN SCREENS. Credits and the detailed
// analytics screen are not in the design's top bar. They are NOT dropped — dropping a working
// screen to match a mock is how a customer loses the page they check their balance on. They moved
// into the account menu under the avatar, which is where a "my account" concern belongs anyway.

import { useEffect, useRef, useState } from 'react'
import { platform } from '../../api.js'
import logoUrl from '../../assets/incruiter-logo.svg'
// [2026-09-04] CHANGED — CreditsIcon, TrendingUpIcon and UsersIcon dropped from this import when
// the account menu's three navigation items moved onto the bar (nav items are text-only). All three
// are still exported from ./icons.jsx and still used elsewhere — PlatformDashboard renders every
// one of them — so nothing was removed, only this file stopped importing them.
// OLD: BellIcon, ChevronLeftIcon, ChevronRightIcon, CreditsIcon, GridIcon, PlusIcon, SignOutIcon,
//      TrendingUpIcon, UsersIcon
// [2026-09-05] CHANGED — ChevronLeftIcon, ChevronRightIcon and GridIcon dropped from this import
// when the rail became a port of the account app's sidebar: the handle now draws that app's own
// arrow-*-nostroke.svg and the top button its my-products-icon-white.svg. All three are still
// exported from ./icons.jsx and are unchanged — this file simply stopped importing them, so
// nothing else that uses them is affected.
// OLD: BellIcon, ChevronLeftIcon, ChevronRightIcon, GridIcon, PlusIcon, SignOutIcon,
import { BellIcon, PlusIcon, SignOutIcon } from './icons.jsx'
// [2026-09-05] ADDED — the rail's links, ported from the account app. See productLinks.js.
// [2026-09-27] CHANGED — generateUrl added, for the new sidebar's "Products" links (the product's
// page on the InCruiter website), exactly as the other products' sidebar builds them.
// OLD: import { accountHref, productHref } from './productLinks.js'
// [2026-09-28] CHANGED — incserveTokenNow added, for the IncServe rule in subscribedProducts.
// OLD: import { accountHref, generateUrl, productHref } from './productLinks.js'
import { accountHref, generateUrl, incserveTokenNow, productHref } from './productLinks.js'
// [2026-09-27] ADDED — the shared "which product was picked" cookie, written on a product click.
import { rememberSelectedProduct } from './selectedProduct.js'
// [2026-09-27] ADDED — the sidebar and top bar every other InCruiter product now renders (the
// shared @contact_incruiter/fcc InCruiterSidebar / InCruiterTopBar), ported — see the file headers.
import { InCruiterSidebar, SidebarSubItem } from './InCruiterSidebar.jsx'
import {
  Bell as LucideBell, CreditCard, Headphones, LayoutDashboard, LogOut, Package, Settings, UserCircle,
} from './lucide.jsx'
// [2026-09-07] ADDED — see roles.js: what this member may be OFFERED, not what they may do.
import { canManageKeys } from '../../roles.js'
// [2026-09-05] ADDED — the account app's OWN sidebar artwork, copied byte-for-byte from
// frontend-account/src/assets/images/. Reproducing the sidebar with different glyphs would leave
// the two products looking different in exactly the place the customer compares them.
//   my-products-icon-white.svg  — Sidebar.jsx:20, the "Product Dashboard" button at the top
//   interrogation.svg           — Sidebar.jsx:16, the Support button at the bottom
//   arrow-right/back-nostroke   — Sidebar.jsx:18-19, the teal collapse handle
import myProductsIcon from '../../assets/my-products-icon-white.svg'
import supportIcon from '../../assets/interrogation.svg'
// [2026-09-24] ADDED — the header bell, avatar caret, default avatar and account-menu icons that the
// other InCruiter products use, so this header matches theirs (asked for by Rujal, 2026-09-24).
// Copied byte-for-byte from the other repos' COMMITTED origin/dev, never redrawn:
//   bell.svg        frontend-account src/assets/images/bell.svg (same blob in incbot, increcruit)
//   arrow-down.svg  frontend-incbot  src/assets/images/arrow-down.svg (same in account, increcruit)
//   *-active / *-inactive.svg  frontend-incbot src/assets/images/header/ (same in increcruit)
//   default-avatar.jpeg  the image every header falls back to when a user has no photo
//     (https://incruiter-media.s3.ap-south-1.amazonaws.com/client1/emailTemplates/file-1659100896378-328095968.jpeg).
//     BUNDLED rather than hot-linked: this app's e2e suites assert a clean console, and a remote
//     image that fails to load on an offline runner logs an error no code here can suppress.
import bellImg from '../../assets/header/bell.svg'
import dropArrow from '../../assets/header/arrow-down.svg'
import defaultAvatar from '../../assets/header/default-avatar.jpeg'
import myProfileInactive from '../../assets/header/myprofile-inactive.svg'
import myProfileActive from '../../assets/header/myprofile-active.svg'
import accountsInactive from '../../assets/header/accounts-inactive.svg'
import accountsActive from '../../assets/header/accounts-active.svg'
import supportMenuInactive from '../../assets/header/support-inactive.svg'
import supportMenuActive from '../../assets/header/support-active.svg'
import settingsInactive from '../../assets/header/settings-inactive.svg'
import settingsActive from '../../assets/header/settings-active.svg'
import logoutInactive from '../../assets/header/logout-inactive.svg'
import logoutActive from '../../assets/header/logout-active.svg'
// arrow-right-nostroke.svg / arrow-back-nostroke.svg are INLINED below rather than imported — see
// RailArrow. The files are still in src/assets/ as the source those two paths were copied from.

// The top bar. `id` is the view key PlatformApp already routes on, so this is a relabelling of
// existing navigation rather than a new router.
//
// [2026-09-04] CHANGED — all SEVEN screens are on the bar now. OLD:
//   const NAV = [
//     { id: 'overview', label: 'Dashboard' },
//     { id: 'reports',  label: 'Proctoring Reports' },
//     { id: 'requests', label: 'API Requests' },
//     { id: 'keys',     label: 'API Keys' },
//     { id: 'members',  label: 'Add Users' },
//   ]
//   const MENU = [
//     { id: 'analytics', label: 'Usage & analytics', Icon: TrendingUpIcon },
//     { id: 'credits',   label: 'Credits & billing', Icon: CreditsIcon },
//     { id: 'members',   label: 'Team members',      Icon: UsersIcon },
//   ]
//
// The Figma's bar has five items, so Usage & Analytics and Credits & Billing were put behind the
// avatar. That was the wrong call: hiding a screen behind an avatar is how a customer stops finding
// the page they check their balance on. Both are back on the bar where they can be seen.
//
// 'members' appeared TWICE — as "Add Users" on the bar and as "Team members" in the menu — two
// labels for one screen, which makes a reader wonder what the difference is. The menu copy is gone;
// "Add Users" on the bar is the single entry point.
//
// ORDER IS BY TASK, not by the Figma's five: what happened (Dashboard, Usage) -> who it happened to
// (Reports, Requests) -> account admin (Keys, Credits, Users).
// [2026-09-07] ADDED — the nav items a VIEWER is not offered.
//
// A viewer's role is defined in app/core/members.py:59 as "reads reports, analytics, credit
// balance. Writes nothing." Every control on the API Keys screen writes — create, revoke, delete
// are all gated at ROLE_ADMIN on the server as of the same change — so the screen is not offered
// rather than offered with nothing on it.
//
// The other five stay. Dashboard, Proctoring Reports, API Requests and Credits & Billing are the
// reading the role exists FOR; hiding them would not be a smaller permission, it would be an empty
// account. Add Users also stays and is READ-ONLY for a viewer: PlatformMembers already hides every
// control behind `my_role` (PlatformMembers.jsx), so a viewer sees who their colleagues are and can
// change nothing — which is worth more than a missing tab when they need to know who to ask.
const VIEWER_HIDDEN = ['keys']

const NAV = [
  { id: 'overview',  label: 'Dashboard' },
  // [2026-09-07] REMOVED FROM THE NAV — commented, not deleted.
  //   { id: 'analytics', label: 'Usage & Analytics' },
  // Asked for. The screen itself is left in place (PlatformAnalytics.jsx is untouched and its
  // route is commented alongside this in PlatformApp.jsx), so restoring the tab is uncommenting
  // two lines rather than rebuilding a page.
  { id: 'reports',   label: 'Proctoring Reports' },
  { id: 'requests',  label: 'API Requests' },
  { id: 'keys',      label: 'API Keys' },
  { id: 'credits',   label: 'Credits & Billing' },
  // [2026-09-07] CHANGED — 'Add Users' taken off the bar.
  //
  // OLD:  { id: 'members',   label: 'Add Users' },
  //
  // WHY: this dashboard's own membership system is now off by default
  // (proctoring-sdk-backend app/config.py, ME_MEMBERS_ENABLED). Who may open InC-Proctor is decided
  // in InCruiter — Roles / Sub User — and this second, separate list was a trap: granting a person
  // InC-Proctor there did nothing until somebody ALSO invited them here, and until then every
  // /me/* call 403'd and their dashboard was blank with no explanation.
  //
  // With the backend flag off, GET /me/members answers 501 (_require_members_enabled), so leaving
  // the tab up would mean a visible menu item that errors on click. Removing the tab is not what
  // removes the capability — the flag is. This just stops advertising a door that is now closed.
  //
  // NOT DELETED, per this repo's rule and so this is genuinely reversible: PlatformMembers.jsx is
  // untouched and its route in PlatformApp.jsx is untouched, so `?view=members` still resolves. Set
  // GATEWAY_ME_MEMBERS_ENABLED=true on the backend and uncomment the line above, and the feature is
  // back exactly as it was — one line here, one value there.
  // { id: 'members',   label: 'Add Users' },
  //
  // [2026-09-08] SUPERSEDED — the tab is BACK, pointing at InCruiter. The block above is kept
  // because its reasoning about the backend flag is still correct; only its conclusion was wrong.
  //
  // Removing the tab was the wrong reading of "use the default Add User". The point was never to
  // take user management away from this dashboard — it was to stop this dashboard having its OWN
  // second list. A customer still needs a way to reach user management from here, and deleting the
  // only route to it leaves them hunting for it in another product.
  //
  // So: same tab, same label, same position — different destination. It opens InCruiter's
  // Roles / Sub User page, the one place every other product's users are already added
  // (frontend-account/src/RouteList/RoutesFile.jsx:285).
  //
  // THE PATH IS STORED, NOT THE URL, and that is not a style choice. `accountUrl` is declared BELOW
  // this array, so calling it here would run before its initialiser and throw "Cannot access
  // 'accountUrl' before initialization" at module load — a blank dashboard, not a broken link. The
  // href is built at render, where the function exists.
  //
  // accountHref applies the environment prefix, so dev links to dev and prod to prod. See
  // productLinks.js for why that derivation exists and what a missing prefix costs.
  { id: 'members', label: 'Add Users', accountPath: '/roles-subuser-list' },
]

// [2026-09-05] CHANGED — the rail is the REAL product list now, not two hardcoded entries.
//
// OLD: IncServeMark and IncProctMark, two hand-drawn SVGs, with IncServe hardwired to
// incserve.incruiter.com and IncProct always marked current. That was a mock: it showed the same
// two products to every customer regardless of what they had bought, and it invented icons this
// product does not use.
//
// The InCruiter account app builds this rail from GET products/productList — each entry carrying a
// name, a subdomain, a status and an ICON URL — filtered by status and the caller's permissions
// (frontend-account/src/component/Layout/Sidebar.jsx:60-70, :95-136). This uses the same list,
// proxied through GET /me/products; see api.js for why it must be proxied and not fetched here.
//
// THE FILTER IS THE ACCOUNT APP'S: hide anything Unsubscribed, order by `position`. A customer who
// has only bought InCProctor therefore sees only InCProctor.
//
// FAILS QUIET. The endpoint answers 200 with an empty list when the subscription system is
// unconfigured, unreachable or refuses, and a rejected promise is swallowed here — so the rail
// renders nothing extra and the dashboard still opens. A navigation strip is not worth failing a
// page for, and the cause is already logged server-side with the upstream status.
function useProducts() {
  const [products, setProducts] = useState([])
  useEffect(() => {
    let live = true
    platform.products()
      .then((data) => {
        if (!live) return
        setProducts((data?.products || [])
          .filter((p) => String(p.status || '').toLowerCase() !== 'unsubscribed')
          .sort((a, b) => Number(a.position || 0) - Number(b.position || 0)))
      })
      .catch(() => { if (live) setProducts([]) })
    return () => { live = false }
  }, [])
  return products
}

// Which entry is THIS app. Matched on `subdomain` because that is the stable key the account app
// keys on too — `name` is display text and is already spelled two ways across the product set.
// [2026-09-05] ADDED — the account menu's destinations.
//
// These are NOT new pages. They are the routes the InCruiter account app's own header navigates to
// (frontend-account/src/component/Layout/Header.jsx:445-496 — navigate('/my-profile'),
// '/accounts-billing', '/support', '/settings'). That header is 556 lines wired to that app's Redux
// store, its notification actions and its logout saga, so it cannot be imported here. What CAN be
// reused is the thing that actually matters: the pages themselves. Linking to them means a customer
// gets the one profile page and the one billing page, not a second copy that drifts.
//
// The host is VITE_ACCOUNT_URL — the same variable PlatformApp already uses to bounce a signed-out
// visitor, so there is no new configuration and it is correct per environment by construction.
// [2026-09-24] CHANGED — each item carries the icon pair the other products' menus show (grey at
// rest, navy on hover). Same four paths, same labels, same order as before; `icon` / `activeIcon`
// are additions.
// OLD:
//   { path: '/my-profile', label: 'My Profile' },
//   { path: '/accounts-billing', label: 'Accounts & Billing' },
//   { path: '/support', label: 'Support' },
//   { path: '/settings', label: 'Settings / Preferences' },
// [2026-09-27] ADDED — the WHOLE product list, unfiltered, for the new sidebar.
//
// useProducts above drops unsubscribed products, which is right for the old rail but not for the
// new sidebar: its "Products" section lists EVERY InCruiter product (frontend-incbot and
// frontend-account both pass the full productData there) and "My Subscriptions" applies its own
// rule. One request, as before — the old `products` list is now derived from this one instead of
// being fetched a second time (see PlatformShell).
function useAllProducts() {
  const [all, setAll] = useState([])
  useEffect(() => {
    let live = true
    platform.products()
      .then((data) => { if (live) setAll(Array.isArray(data?.products) ? data.products : []) })
      .catch(() => { if (live) setAll([]) })
    return () => { live = false }
  }, [])
  return all
}

// [2026-09-27] ADDED — copied from the other products' sidebar (frontend-incbot / frontend-account
// src/component/Layout/Sidebar.jsx): the product page on the InCruiter website that the sidebar's
// "Products" list opens, and which products count as subscriptions. Kept identical so the two lists
// show the same things here as in IncBot. NOTE, inherited as-is: products missing from this map
// (InC-Proctor among them) fall back to the crowdstaffing page — that is the shared sidebar's
// behaviour in every product today, and fixing it belongs in the shared map, not in one copy.
const PRODUCT_LINKS = {
  incexit: 'exit-interview-services',
  incserve: 'interview-as-a-service',
  incbot: 'ai-interview',
  incfeed: 'interview-scheduling-software',
  incvid: 'video-interview-portal',
  incscreen: 'ai-recruiter',
  incsource: 'crowdstaffing',
}
const getProductLink = (subdomain) => PRODUCT_LINKS[subdomain?.toLowerCase()] || 'crowdstaffing'
const IS_SUBSCRIPTION = (p) => ['subscribed', 'active', 'expired']
  .includes(String(p?.status || '').toLowerCase())
const productName = (p) => p?.name?.split(':')[0] || p?.subdomain || ''

const ACCOUNT_LINKS = [
  { path: '/my-profile', label: 'My Profile', icon: myProfileInactive, activeIcon: myProfileActive },
  { path: '/accounts-billing', label: 'Accounts & Billing', icon: accountsInactive, activeIcon: accountsActive },
  { path: '/support', label: 'Support', icon: supportMenuInactive, activeIcon: supportMenuActive },
  { path: '/settings', label: 'Settings / Preferences', icon: settingsInactive, activeIcon: settingsActive },
]

// [2026-09-05] CHANGED — the account host comes from productLinks.js now, which derives it from
// this app's OWN hostname instead of from VITE_ACCOUNT_URL.
//
// OLD:
//   const accountUrl = (path) => {
//     const base = String(import.meta.env?.VITE_ACCOUNT_URL || '').replace(/\/+$/, '')
//     return base ? `${base}${path}` : null
//   }
//
// VITE_ACCOUNT_URL IS EMPTY IN THE DEPLOYED DEV BUILD — verified 2026-09-05 by reading the live
// bundle (see the header of productLinks.js). So this returned null on every call, and the callers
// below are all guarded with `accountUrl(…) && …`: the notifications bell and every item in the
// account menu were silently ABSENT from the deployed dashboard. Nothing errored and nothing was
// logged; the controls just were not there. Deriving the host from our own hostname removes the
// configuration step that was missed, so they come back.
const accountUrl = (path) => accountHref(path)

// [2026-09-05] ADDED — the environment prefix, DERIVED from VITE_ACCOUNT_URL.
//
// THE BUG THIS FIXES: product links were built as `https://{subdomain}.incruiter.com`, with no
// prefix at all. On dev that sent someone to the PRODUCTION IncScreen instead of
// dev.v1.incscreen.incruiter.com — a link out of the environment you are working in, which is far
// worse than a broken link because it silently succeeds against real customer data.
//
// The account app keeps these prefixes in generateUrl() keyed on REACT_APP_ENV
// (frontend-account/src/utils/helper.jsx:744-790): '' for prod, 'test.v1.' for test/preprod,
// 'dev.v2.' for dev2, 'dev.v1.' otherwise. This app has no such env variable and adding one would
// be a second thing to set and to get wrong. But VITE_ACCOUNT_URL ALREADY CARRIES THE PREFIX —
// https://dev.v1.account.incruiter.com — so it is read from there and cannot disagree with the
// environment the app is actually deployed in.
//
// Falls back to '' (production hosts) only when the account URL is absent or unrecognisable, which
// is also when the rail renders no external links at all.
// [2026-09-05] CHANGED — moved into productLinks.js, and both halves of it were wrong. The full
// account of why is in that file's header; the short version is that it read VITE_ACCOUNT_URL,
// which is EMPTY in the deployed dev build, so every product link pointed at PRODUCTION.
//
// OLD:
//   function envPrefix() {
//     const base = String(import.meta.env?.VITE_ACCOUNT_URL || '')
//     try {
//       const host = new URL(base).hostname
//       const match = host.match(/^(.*?)account\.incruiter\.com$/)
//       return match ? match[1] : ''
//     } catch {
//       return ''
//     }
//   }
//
//   // The account app links to `${generateUrl(product)}/dashboard`, not to the bare host — the
//   // bare host lands on a marketing or redirect page rather than the product.
//   const productUrl = (subdomain) =>
//     `https://${envPrefix()}${String(subdomain).toLowerCase()}.incruiter.com/dashboard`

const IS_PROCTOR = (p) => String(p.subdomain || '').toLowerCase().startsWith('incproct')

/** [2026-09-05] ADDED — the chevron inside the rail's teal handle.
 *
 *  The two paths are copied verbatim from the account app's arrow-right-nostroke.svg and
 *  arrow-back-nostroke.svg (src/assets/, same files, byte-for-byte).
 *
 *  IT HAS TO BE INLINE, and this is worth stating because <img src={arrow}> is the obvious thing to
 *  write and it renders NOTHING. Those files are called "nostroke" because they declare no `stroke`
 *  and no `fill` — the account app supplies both from CSS (`.sidebar-back-arrow`,
 *  `.sidebar-right-arrow` in _sidebar.scss:139-144) and can only do so because it inlines them with
 *  CRA's `ReactComponent` import. An <img> is a separate document that outside CSS cannot reach, so
 *  the stroke stays at its `none` default and the handle renders as an empty teal circle. That is
 *  exactly what the first build of this rail did, and it looks like a missing asset rather than a
 *  styling bug. The stroke is therefore set here, to the white those CSS rules use. */
const RailArrow = ({ back }) => (
  <svg width={back ? 12 : 10} height={back ? 18 : 15}
       viewBox={back ? '0 0 12 18' : '0 0 10 15'}
       aria-hidden="true" focusable="false"
       stroke="#fff" fill="#fff" strokeWidth="2" strokeLinecap="round">
    {back ? (
      <>
        <path d="M9.42994 15.0977L2.96094 8.62866" />
        <path d="M9.42994 2.15991L2.96094 8.62891" />
      </>
    ) : (
      <>
        <path d="M2 13.3691L7.58 7.78914" />
        <path d="M2 2.20906L7.58 7.78906" />
      </>
    )}
  </svg>
)


/** [2026-09-04] ADDED — the avatar placeholder the Figma draws: a neutral person silhouette, not
 *  initials.
 *
 *  WHY NOT INITIALS, which was the first version. GET /me returns no avatar URL
 *  (app/modules/me/route.py:187-193 returns company, code and signed_in_as{name,email} only), so
 *  there is no photo to show and never will be until the API carries one. A letter derived from an
 *  email local-part is frequently not the person's initial at all — "ops@", "no-reply@",
 *  "rujal.lde@" — and a confidently wrong initial on every page is worse than an honest blank.
 *  The account menu beside it shows the real name and address. */
const AvatarPlaceholder = () => (
  <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false" width="100%" height="100%">
    <circle cx="20" cy="20" r="20" fill="#e7eaf0" />
    <circle cx="20" cy="15.5" r="6.2" fill="#a9b4c4" />
    <path d="M6.6 35.5a13.8 13.8 0 0126.8 0A20 20 0 0120 40a20 20 0 01-13.4-4.5z" fill="#a9b4c4" />
  </svg>
)

export default function PlatformShell({
  view,
  setView,
  session,
  onSignOut,
  onCreateKey,          // optional — renders the header's "Create API Key +" when supplied,
                        //            and only for a member allowed to create one — see below
  title,
  blurb,
  actions,              // optional right-hand controls on the page head (e.g. the range picker)
  children,
}) {
  // [2026-09-07] ADDED — what this member may be OFFERED. Not the authorisation; see roles.js.
  const mayManageKeys = canManageKeys(session)
  const navItems = mayManageKeys ? NAV : NAV.filter((item) => !VIEWER_HIDDEN.includes(item.id))

  const [menuOpen, setMenuOpen] = useState(false)
  // Collapsing the rail is presentational and deliberately NOT persisted: it is one click to
  // restore, and a preference stored per browser is a support question the first time someone
  // opens the dashboard on a second machine and finds it "different".
  // [2026-09-05] CHANGED — starts COLLAPSED, and `railOpen` now means "the wide panel is open".
  // OLD: useState(true), where true meant the icons-and-captions rail was showing.
  // The account app's sidebar opens collapsed (`activeSidebar` is false until someone presses the
  // handle), and every other InCruiter product therefore greets you with the narrow icon rail. This
  // one greeting you with a wide one is the difference a customer notices first.
  const [railOpen, setRailOpen] = useState(false)
  const menuRef = useRef(null)
  // [2026-09-27] CHANGED — one products request feeds both the new sidebar and the (switched-off)
  // old rail, instead of the old rail fetching its own filtered copy.
  // OLD: const products = useProducts()
  const allProducts = useAllProducts()
  const products = allProducts
    .filter((p) => String(p.status || '').toLowerCase() !== 'unsubscribed')
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))

  // [2026-09-27] ADDED — the new sidebar's collapse state, with IncBot's rules
  // (frontend-incbot Sidebar.jsx): starts COLLAPSED, collapses again on every page change, and is
  // forced collapsed on narrow screens (<= 993px) without fighting a manual expand. Not persisted,
  // for the reason the note on railOpen above gives.
  const [sbCollapsed, setSbCollapsed] = useState(true)
  useEffect(() => { setSbCollapsed(true) }, [view])
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mediaQuery = window.matchMedia('(max-width: 993px)')
    if (mediaQuery.matches) setSbCollapsed(true)
    const handleChange = (e) => { if (e.matches) setSbCollapsed(true) }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // A menu that only closes by clicking its own trigger is a menu people end up navigating around.
  // Escape and an outside click both close it; `mousedown` rather than `click` so the menu is gone
  // before whatever was underneath receives the event.
  useEffect(() => {
    if (!menuOpen) return undefined
    const onDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false)
    }
    const onKey = (event) => { if (event.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const go = (next) => { setMenuOpen(false); setView(next) }

  // [2026-09-27] ADDED — the new sidebar's contents: IncBot's exactly (frontend-incbot
  // src/component/Layout/Sidebar.jsx navItems / otherItems / profileItems), with InC-Proctor as the
  // product "you are in". Account-portal destinations are full URLs, as IncBot passes them.
  // [2026-09-28] CHANGED — IncServe is listed when this browser holds the IncServe login cookie.
  //
  // REPORTED by Rujal (test, 2026-09-27/28): IncServe missing here while the account app and
  // IncScreen list it. Confirmed in the browser: `incserveToken_test` present, yet /me/products
  // returned InCServe "Unsubscribed". The subscription system decides InCServe's status from that
  // cookie alone: forwarded -> Subscribed, missing -> Unsubscribed (subscription-system
  // ProductService.ts:395-411, 457-458). The account app and IncScreen forward it from the
  // browser; this dashboard depends on its backend doing so, which needs a per-environment
  // setting (GATEWAY_INCSERVE_COOKIE_NAME) that cannot be changed right now. So the same rule is
  // applied here, from the same cookie (productLinks.js incserveTokenNow, env-matched). Only
  // InCServe is affected, and only in the direction of SHOWING it; IncServe is outside the role
  // permission system everywhere (me/route.py _may_open_product, account helper.jsx
  // hasProductAccess), so no permission is bypassed. Its click already goes through the same
  // cookie check (productHref).
  // OLD:
  // const subscribedProducts = allProducts
  //   .filter(IS_SUBSCRIPTION)
  const subscribedProducts = allProducts
    .filter((p) => IS_SUBSCRIPTION(p)
      || (String(p?.subdomain || '').toLowerCase() === 'incserve' && Boolean(incserveTokenNow())))
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
  // [2026-09-27] CHANGED — save the chosen product in the shared `iCpD_<env>` cookie first, then
  // navigate: the same two steps, in the same order, as the account app, IncBot and InCScreen
  // (src/component/Layout/Sidebar.jsx navigateToDashboard in each). Without it the account app
  // opened whichever product was saved last — IncVid for an IncFeed click, or the product picker.
  // Why and how: selectedProduct.js. Fail-open: if saving fails or stalls, the click still goes
  // where it always went, at most 1s later.
  // OLD:
  // const openProductDashboard = (p) => {
  //   if (IS_PROCTOR(p)) { go('overview'); return }
  //   const href = productHref(p)
  //   if (href) window.location.href = href
  // }
  const openProductDashboard = (p) => {
    const saved = Promise.race([
      rememberSelectedProduct(p),
      new Promise((resolve) => { setTimeout(resolve, 1000) }),
    ]).catch(() => {})
    if (IS_PROCTOR(p)) { go('overview'); return }
    const href = productHref(p)
    if (href) saved.then(() => { window.location.href = href })
  }
  const openProductPage = (p) => window.open(
    `${generateUrl('website')}/${getProductLink(p.subdomain)}`, '_blank', 'noopener,noreferrer')
  // The collapsed-rail product buttons, verbatim from IncBot's Sidebar.jsx (inline styles).
  const collapsedProductButtons = (list, isActive, onPick) => (list.length > 0 ? (
    <div>
      {list.map((p) => {
        const active = isActive(p)
        const name = productName(p)
        return (
          <button key={p._id || p.id || p.subdomain} onClick={() => onPick(p)} title={name}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center',
                    paddingTop: 8, paddingBottom: 8,
                    background: active ? 'rgba(18,182,188,0.08)' : 'transparent',
                    border: 'none', cursor: 'pointer', outline: 'none',
                  }}>
            {p.icon ? (
              <img src={p.icon} alt={name}
                   style={{
                     width: 22, height: 22, borderRadius: 4, objectFit: 'cover',
                     outline: active ? '2px solid #12b6bc' : 'none', outlineOffset: 2,
                   }} />
            ) : (
              <div style={{
                width: 22, height: 22, borderRadius: 4,
                background: active ? 'rgba(18,182,188,0.15)' : 'rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: active ? '#12b6bc' : '#9CA3AF',
              }}>
                {name.charAt(0)}
              </div>
            )}
          </button>
        )
      })}
    </div>
  ) : null)
  const subscriptionContent = subscribedProducts.length > 0 ? (
    <div>
      {subscribedProducts.map((p) => (
        <SidebarSubItem key={p._id || p.id || p.subdomain} name={productName(p)} icon={p.icon}
                        active={IS_PROCTOR(p)} onClick={() => openProductDashboard(p)} />
      ))}
    </div>
  ) : null
  const allProductsContent = allProducts.length > 0 ? (
    <div>
      {allProducts.map((p) => (
        <SidebarSubItem key={p._id || p.id || p.subdomain} name={productName(p)} icon={p.icon}
                        active={false} onClick={() => openProductPage(p)} />
      ))}
    </div>
  ) : null
  const sidebarNavItems = [
    { label: 'Product Dashboard', href: accountHref('/product'), Icon: LayoutDashboard },
    {
      label: 'My Subscriptions', href: accountHref('/subscriptions'), Icon: CreditCard,
      badge: subscribedProducts.length || undefined, hasChevron: true,
      expandedContent: subscriptionContent,
      collapsedExpandedContent: collapsedProductButtons(subscribedProducts, IS_PROCTOR, openProductDashboard),
    },
    {
      label: 'Products', href: accountHref('/products'), Icon: Package,
      badge: allProducts.length || undefined, hasChevron: true,
      expandedContent: allProductsContent,
      collapsedExpandedContent: collapsedProductButtons(allProducts, () => false, openProductPage),
    },
    { label: 'Settings/Preferences', href: accountHref('/settings'), Icon: Settings },
  ]
  const sidebarOtherItems = [
    {
      label: 'Support', href: '/support', Icon: Headphones,
      onClick: () => window.open(accountHref('/support'), '_blank', 'noopener,noreferrer'),
    },
    { label: 'Logout', href: '/logout', Icon: LogOut, onClick: onSignOut },
  ]
  const sidebarUser = {
    name: session?.signed_in_as?.name || session?.company || '',
    role: session?.company || '',
    avatarUrl: null,
  }
  const sidebarProfileTitle = [session?.signed_in_as?.name, session?.signed_in_as?.email,
    session?.code ? `${session?.company || 'Client'} · ${session.code}` : null]
    .filter(Boolean).join('\n')

  // InCruiterTopBar's tab style (fcc src/shared/InCruiterTopBar.tsx), verbatim values.
  const topbarLinkStyle = (active) => ({
    position: 'relative', height: '100%', padding: '0 16px', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #12b6bc' : '2px solid transparent', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: active ? 700 : 500,
    color: active ? '#12b6bc' : '#6b7280', whiteSpace: 'nowrap', outline: 'none',
    transition: 'color 0.15s, border-color 0.15s',
  })
  const topbarHover = (active) => ({
    onMouseEnter: (e) => { if (!active) e.currentTarget.style.color = '#374151' },
    onMouseLeave: (e) => { if (!active) e.currentTarget.style.color = '#6b7280' },
  })

  return (
    // [2026-09-27] CHANGED — `pf-shell-v2` / `sb-open` added for the new sidebar's layout
    // (platform-theme.css). OLD: <div className="pf-app">
    <div className={`pf-app pf-shell-v2${sbCollapsed ? '' : ' sb-open'}`}>
      {/* [2026-09-27] ADDED — the InCruiter sidebar, as in IncBot and the account portal: full
          height on the left, InCruiter logo and ☰ at the top, the company switcher, MAIN MENU
          (Product Dashboard, My Subscriptions, Products, Settings/Preferences) and OTHER (Support,
          Logout). Asked for by Rujal, 2026-09-27. The old navy rail below is switched OFF, not
          deleted. */}
      <div className="pf-sb-host">
        <InCruiterSidebar
          logoSrc={logoUrl}
          user={sidebarUser}
          navItems={sidebarNavItems}
          otherItems={sidebarOtherItems}
          activeHref={typeof window !== 'undefined' ? window.location.pathname : '/'}
          onNavigate={(href) => { if (/^https?:\/\//i.test(href)) window.location.href = href }}
          profileItems={[
            { label: 'My Profile', Icon: UserCircle, href: '/my-profile', link: accountHref('/my-profile') },
            { label: 'Accounts & Billing', Icon: CreditCard, href: '/accounts-billing', link: accountHref('/accounts-billing') },
          ]}
          collapsed={sbCollapsed}
          onToggleCollapse={setSbCollapsed}
          defaultExpandedItem={accountHref('/subscriptions')}
          profileTitle={sidebarProfileTitle}
        />
      </div>

      {/* [2026-09-27] CHANGED — the old rail is OFF (`false &&`), replaced by the sidebar above.
          Kept, not deleted, so going back is removing two words. The `false && (` sits after the
          rail's own long comment, directly on the <aside>: a comment cannot follow it inside the
          parentheses (see the note on the header below). */}
      {/* ── the product rail ─────────────────────────────────────────────────────────────────────
          [2026-09-05] CHANGED — this is now a PORT of the InCruiter account app's sidebar
          (frontend-account/src/component/Layout/Sidebar.jsx:70-265), not a rail designed here.

          WHY IT WAS REPLACED. Every InCruiter product — IncScreen, IncBot, IncFeed — renders this
          same sidebar, so a customer moving between them sees one piece of furniture that does not
          change. InC-Proctor rendered something else: a different navy, square white tiles instead
          of bare icons, a different chevron in a different place, a "?" typed as text instead of
          the Support glyph. It was the only product that looked like a different company's app.

          Matching it is not cosmetic. The rail is how someone leaves this product, and a control
          that has moved is a control that has to be found again.

          WHAT WAS PORTED, and the account app line each piece comes from:
            · the collapsed rail's structure — Sidebar.jsx:82-265
            · the "Product Dashboard" button + white rule — Sidebar.jsx:84-97 (.sidebar-collapse-
              header, .division-div)
            · a product row: bare icon, 40px WHITE DISC only when current, 10px caption
              — Sidebar.jsx:120-136 (.product-icon-container, .f-10)
            · the teal collapse handle at top:5em, right:-0.8em — Sidebar.jsx:77-83 (.sidebarCollapse)
            · the Support button on a white top rule — Sidebar.jsx:496-516 (.interogation-main)
          Every measurement and colour is in _sidebar.scss / _global.scss; see platform-theme.css,
          which cites the line for each one.

          THE OLD MARKUP IS NOT DELETED — it is preserved verbatim at the bottom of this file,
          under OLD_RAIL_MARKUP, so restoring the Figma rail is one edit.

          ONE DELIBERATE DIFFERENCE, stated rather than hidden: the account app's handle toggles
          between this rail and a wide sidebar full of ITS OWN routes (My Interviews, Positions,
          Onboarding…). Those pages do not exist here, so the wide state shows the same product
          list with names beside the icons, using the account app's own `.services` row
          (Sidebar.jsx:296-450). Building anything else into that panel would be the invention this
          change exists to remove. */}
      {false && (
      <aside className={`pf-rail${railOpen ? ' wide' : ''}`}>
        {/* .sidebarCollapse — Sidebar.jsx:77-83. In the account app this is a bare <div> with an
            onClick; here it is a <button> so it can be reached by keyboard and announces itself.
            That is an accessibility fix, not a redesign: it is the same 30px teal circle in the
            same place. */}
        <button className="pf-rail-toggle" onClick={() => setRailOpen((open) => !open)}
                aria-label={railOpen ? 'Collapse the product list' : 'Expand the product list'}
                aria-expanded={railOpen}>
          <RailArrow back={railOpen} />
        </button>

        <div className="pf-rail-main">
          {/* The account app's "Product Dashboard" button, which opens the product picker at
              /product on the account portal. In the collapsed rail it is the icon alone
              (Sidebar.jsx:86-96); expanded, the icon and the label (Sidebar.jsx:270-280). */}
          <div className="pf-rail-head">
            <a href={accountHref('/product')} className="pf-rail-head-btn" title="Product Dashboard">
              <img src={myProductsIcon} alt="" aria-hidden="true" />
              {railOpen && <span className="pf-rail-head-label">Product Dashboard</span>}
            </a>
            <div className="pf-rail-rule" />
          </div>

          {railOpen && (
            <>
              <div className="pf-rail-goto">Go to Product</div>
              <div className="pf-rail-rule" />
            </>
          )}

          <nav className="pf-rail-products" aria-label="InCruiter products">
            {products.map((p) => {
              const current = IS_PROCTOR(p)
              // The CURRENT product is a button that goes nowhere — it is where you already are.
              // Every other one goes wherever the account app's sidebar sends it; productHref is
              // that same branch chain. `current` is decided on `subdomain`, the stable key, not
              // on the display name.
              const Tag = current ? 'button' : 'a'
              const extra = current
                ? { type: 'button', 'aria-current': 'page', title: `${p.name} — you are here` }
                : { href: productHref(p), title: 'Go to Dashboard' }
              return (
                <Tag key={p.id || p.subdomain}
                     className={`pf-rail-item${current ? ' active' : ''}`} {...extra}>
                  {/* THE DISC IS ONLY DRAWN FOR THE CURRENT PRODUCT — .product-icon-container is
                      applied conditionally in the account app (Sidebar.jsx:120-131) and the other
                      icons sit bare on the navy. The previous rail gave every product a white tile,
                      which is why "which product am I in?" was answered by a shape difference
                      nobody could see. */}
                  <span className={`pf-rail-icon${current ? ' disc' : ''}`}>
                    {/* The icon is a URL from the subscription system. onError hides a broken image
                        rather than leaving the browser's torn-page glyph in a navy rail; the name
                        beside it still identifies the product, so nothing is lost. */}
                    {p.icon
                      ? <img src={p.icon} alt="" aria-hidden="true"
                             onError={(e) => { e.currentTarget.style.display = 'none' }} />
                      : <span className="pf-rail-initial" aria-hidden="true">
                          {String(p.name || '?').charAt(0)}
                        </span>}
                  </span>
                  <span className="pf-rail-name">{p.name}</span>
                </Tag>
              )
            })}
          </nav>
        </div>

        {/* .active-sidebar-downdiv / .interogation-main — Sidebar.jsx:496-516. The account app
            navigates to its own /support route; this app has no such page, so it links to the
            account portal's. Same destination, same button. */}
        <div className="pf-rail-foot">
          <a className="pf-rail-support" href={accountHref('/support')} title="Support">
            <img src={supportIcon} alt="" aria-hidden="true" />
            {railOpen && <span>Support</span>}
          </a>
        </div>
      </aside>
      )}

      {/* ── the top bar ──────────────────────────────────────────────────── */}
      {/* [2026-09-27] ADDED — the top bar the other products render (fcc InCruiterTopBar): 52px,
          white, a soft drop shadow, the product's own button on the left, tabs on the right in grey
          with the active one teal and underlined, and a small grey bell. No logo and no profile
          picture: in the new layout both live in the sidebar. Every value is InCruiterTopBar's.
          It keeps `pf-header` so the existing position and Create API Key sizing rules still apply;
          `pf-topbar` moves its left edge to the sidebar's (platform-theme.css). The nav keeps its
          "Main" label and aria-current, which the tests and screen readers rely on. */}
      <header className="pf-header pf-topbar"
              style={{
                height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', background: '#fff',
                borderBottom: '1px solid rgba(0,0,0,0.07)', padding: '0 12px', gap: 16,
                filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.161))',
              }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {onCreateKey && mayManageKeys && (
            <button className="pf-btn" onClick={onCreateKey} aria-label="Create API Key">
              <span className="pf-btn-label">Create API Key</span> <PlusIcon />
            </button>
          )}
        </div>

        <nav aria-label="Main"
             style={{
               display: 'flex', alignItems: 'stretch', gap: 0, height: '100%', flex: 1,
               justifyContent: 'flex-end', marginRight: 8,
             }}>
          {navItems.map((item) => {
            const href = item.accountPath ? accountUrl(item.accountPath) : null
            const active = !href && view === item.id
            return href ? (
              // An account-portal page (Add Users): a real link, styled as a tab. `display: flex`
              // centres it vertically the way a <button> centres its label on its own.
              // [2026-09-27] CHANGED — opens in a NEW TAB, as IncBot's "Add Users" does
              // (frontend-incbot src/component/Layout/Header.jsx:166-174,
              // `window.open(…/roles-subuser-list, '_blank', 'noopener,noreferrer')`). In the same
              // tab, the account app's own "Dashboard" button then had no way back here: its top bar
              // has no InC-Proctor branch (frontend-account Layout/Header.jsx:198-233), so it went to
              // account /dashboard, which shows the product picker when the visitor arrived from
              // InC-Proctor under 15s earlier (utils/foreignProductHop.js). A new tab leaves this
              // one on InC-Proctor. noopener + noreferrer, exactly as IncBot passes them.
              // OLD: <a key={item.id} href={href}
              <a key={item.id} href={href} target="_blank" rel="noopener noreferrer"
                 style={{ ...topbarLinkStyle(false), display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                 {...topbarHover(false)}>
                {item.label}
              </a>
            ) : (
              <button key={item.id} type="button"
                      aria-current={active ? 'page' : undefined}
                      onClick={() => go(item.id)}
                      style={topbarLinkStyle(active)} {...topbarHover(active)}>
                {item.label}
              </button>
            )
          })}
        </nav>

        <div style={{ position: 'relative', flexShrink: 0 }}>
          {accountUrl('/notification') && (
            <button type="button" aria-label="Notifications" title="Notifications"
                    onClick={() => window.open(accountUrl('/notification'), '_blank', 'noopener,noreferrer')}
                    style={{
                      width: 32, height: 32, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', borderRadius: 8, border: 'none',
                      background: 'transparent', color: '#9ca3af', cursor: 'pointer',
                      position: 'relative', outline: 'none',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af' }}>
              <LucideBell size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </header>

      {/* ── the header ───────────────────────────────────────────────────── */}
      {/* [2026-09-27] CHANGED — the old header (logo, Create API Key, navy tabs, bell image and the
          profile-picture menu) is OFF, replaced by the top bar above and the sidebar. Kept, not
          deleted: removing `false && (` and its `)` brings it back. */}
      {false && (
      <header className="pf-header">
        <img className="pf-logo" src={logoUrl} alt="InCruiter" />

        {/* The label is wrapped so the narrow-screen rule can drop it to the icon alone. The
            aria-label is on the BUTTON, so the control keeps its name either way.

            TWO TRAPS ALREADY HIT WHILE WRITING THIS COMMENT, both worth stating plainly:
            1. The comment must sit OUTSIDE the `&&`. Inside the parentheses a brace-slash-star
               block is an object literal, not a comment, and the file fails to parse.
            2. Never write a star-slash-brace sequence inside a JSX comment, even as an example.
               It closes the comment early, and everything after it becomes a TEXT NODE rendered
               into the page — here it became a 502px-wide flex item in the header that shoved the
               nav off the right edge. It builds cleanly and looks like a layout bug. */}
        {/* [2026-09-07] CHANGED — `&& mayManageKeys`. This button is on EVERY screen, so without
            it a viewer met a Create API Key control on the dashboard itself — the most prominent
            action in the product, and a guaranteed 403.
            OLD: {onCreateKey && ( */}
        {onCreateKey && mayManageKeys && (
          <button className="pf-btn" onClick={onCreateKey} aria-label="Create API Key">
            <span className="pf-btn-label">Create API Key</span> <PlusIcon />
          </button>
        )}

        <nav className="pf-nav" aria-label="Main">
          {/* [2026-09-07] CHANGED — `navItems`, not NAV, so a viewer is not offered API Keys.
              OLD: {NAV.map((item) => ( */}
          {/* [2026-09-08] CHANGED — an item carrying `accountPath` renders as a link OUT to the
              InCruiter account app instead of switching view. Today that is Add Users only.

              OLD:
                {navItems.map((item) => (
                  <button key={item.id} className={...} onClick={() => go(item.id)}>
                    {item.label}
                  </button>
                ))}

              FALLS BACK TO THE BUTTON when accountUrl returns nothing. VITE_ACCOUNT_URL has been
              empty in a deployed build before (see the note above accountUrl), and a nav item that
              silently vanishes is worse than one that opens the old screen — so an unresolvable
              link degrades to exactly the previous behaviour rather than disappearing. */}
          {navItems.map((item) => {
            const href = item.accountPath ? accountUrl(item.accountPath) : null
            return href ? (
              <a key={item.id} className="pf-nav-item" href={href}>
                {item.label}
              </a>
            ) : (
              <button key={item.id}
                      className={`pf-nav-item${view === item.id ? ' active' : ''}`}
                      aria-current={view === item.id ? 'page' : undefined}
                      onClick={() => go(item.id)}>
                {item.label}
              </button>
            )
          })}
        </nav>

        <div className="pf-header-actions" ref={menuRef}>
          {/* aria-label, because this control IS icon-only — see the note in icons.jsx: the button
              speaks, the glyph does not. */}
          {/* [2026-09-05] CHANGED — the bell was a button that did nothing and a red dot that was
              always lit, which is a notification indicator that has never seen a notification. It
              now OPENS THE EXISTING notification page in the account app
              (frontend-account Header.jsx:handleReadAllNotification -> navigate('/notification')).
              The dot is gone with it: this app cannot know whether anything is unread without the
              account app's notification API, and a permanently-lit dot trains people to ignore it.
              OLD: <button className="pf-bell" aria-label="Notifications"> … pf-bell-dot */}
          {/* [2026-09-24] CHANGED — the bell is the SAME bell.svg as every other InCruiter header
              (frontend-account / incbot / increcruit), and it opens the account app's
              notifications in a new tab, as incbot and increcruit do for every account page.
              OLD:
                <a className="pf-bell" href={accountUrl('/notification')}
                   aria-label="Notifications" title="Notifications">
                  <BellIcon />
                </a> */}
          {accountUrl('/notification') && (
            <a className="pf-bell" href={accountUrl('/notification')}
               target="_blank" rel="noopener noreferrer"
               aria-label="Notifications" title="Notifications">
              <img src={bellImg} alt="" aria-hidden="true" />
            </a>
          )}

          {/* [2026-09-04] CHANGED — OLD: {initials(session)}. See AvatarPlaceholder above. */}
          {/* [2026-09-24] CHANGED — the avatar the other products show: a 40px round photo (their
              shared default image, since GET /me carries no photo) and the grey caret beside it.
              Same button, same aria-label — e2e/platform-dashboard.spec.js opens the menu by it.
              OLD:
                <button className="pf-avatar" …><AvatarPlaceholder /></button> */}
          {/* [2026-09-25] CHANGED — `title` added: the signed-in name, email and company · code, on
              hover. They used to be the tooltip of the company-name heading inside the menu, which
              is now switched off (below), and they are still what someone checks when a number
              looks like it belongs to the wrong account. aria-label is unchanged, so the button's
              accessible name — which e2e opens the menu by — is still "Account menu". */}
          <button className="pf-avatar pf-profile" onClick={() => setMenuOpen((open) => !open)}
                  aria-haspopup="menu" aria-expanded={menuOpen}
                  aria-label="Account menu"
                  title={[session?.signed_in_as?.name, session?.signed_in_as?.email,
                          session?.code ? `${session?.company || 'Client'} · ${session.code}` : null]
                    .filter(Boolean).join('\n') || undefined}>
            <span className="pf-profile-img">
              <img src={defaultAvatar} alt="" aria-hidden="true" />
            </span>
            <img className="pf-drop-arrow" src={dropArrow} alt="" aria-hidden="true" />
          </button>

          {/* [2026-09-04] CHANGED — the menu is now identity + sign out only; its three navigation
              items moved onto the bar (see the NAV note above). It is deliberately KEPT rather than
              collapsed into a bare sign-out button: it is the only place the signed-in address and
              the company code are shown, which is what someone checks when a report looks like it
              belongs to the wrong tenant. */}
          {/* [2026-09-24] CHANGED — the menu now has the other products' layout (frontend-incbot
              Header.jsx:311-470): the company name as its heading, then My Profile, Accounts &
              Billing, Support, Settings / Preferences and Logout, each with its grey icon that
              turns navy on hover, the account pages opening in a new tab. The signed-in email and
              the company code are KEPT, as the heading's tooltip, for the reason the 2026-09-06
              note below gives — they are what someone checks when a number looks like it belongs
              to the wrong account. The block below this one is the previous menu, kept for
              reference and never rendered.
              OLD: {menuOpen && ( <div className="pf-menu" role="menu"> … ) */}
          {menuOpen && (
            <div className="pf-menu pf-profile-menu" role="menu">
              {/* [2026-09-25] CHANGED — the company-name heading at the top of this menu is OFF.
                  Asked for by Rujal: remove the "InCruiter" name shown in the right corner by the
                  profile icon. `false &&` rather than a deletion, so it is one edit away from
                  coming back. Its tooltip (name, email, company · code) moved to the avatar
                  button above, so that information is still one hover away.
                  OLD: rendered unconditionally — the same block without `false &&`. */}
              {false && (
              <div className="pf-menu-company"
                   title={[session?.signed_in_as?.name, session?.signed_in_as?.email,
                           session?.code ? `${session?.company || 'Client'} · ${session.code}` : null]
                     .filter(Boolean).join('\n')}>
                {session?.company || session?.signed_in_as?.name || session?.signed_in_as?.email
                  || 'Signed in'}
              </div>
              )}
              {ACCOUNT_LINKS.map((item) => accountUrl(item.path) && (
                <a key={item.path} role="menuitem" className="pf-menu-item"
                   href={accountUrl(item.path)} target="_blank" rel="noopener noreferrer"
                   onClick={() => setMenuOpen(false)}>
                  <img className="pf-menu-icon" src={item.icon} alt="" aria-hidden="true" />
                  <img className="pf-menu-icon on" src={item.activeIcon} alt="" aria-hidden="true" />
                  {item.label}
                </a>
              ))}
              <button role="menuitem" className="pf-menu-item" onClick={onSignOut}>
                <img className="pf-menu-icon" src={logoutInactive} alt="" aria-hidden="true" />
                <img className="pf-menu-icon on" src={logoutActive} alt="" aria-hidden="true" />
                Logout
              </button>
            </div>
          )}
          {false && menuOpen && (
            <div className="pf-menu" role="menu">
              {/* [2026-09-06] CHANGED — show the signed-in EMAIL as its own line.
                  OLD:
                    <strong>{session?.signed_in_as?.name || session?.signed_in_as?.email || 'Signed in'}</strong>
                    <span>{session?.company || 'Client'}{session?.code ? ` · ${session.code}` : ''}</span>

                  The email was only ever a FALLBACK for a missing name, so on every real account —
                  which always has a name — it never rendered at all. The shell revamp dropped the
                  old `.who-email` line and nothing replaced it, so this menu became the one place
                  that names the person without saying WHICH LOGIN they are.

                  That matters here more than on most dashboards: this screen shows credits and
                  billing, several of our users belong to more than one company, and the first thing
                  someone checks when the numbers look wrong is which account they are actually in.
                  A name does not answer that; an email does.

                  Rendered only when it EXISTS AND DIFFERS from the line above, so an account with
                  no name (where the email is already the heading) does not show it twice. */}
              <div className="pf-menu-who">
                <strong>{session?.signed_in_as?.name || session?.signed_in_as?.email || 'Signed in'}</strong>
                {session?.signed_in_as?.email
                  && session.signed_in_as.email !== session?.signed_in_as?.name && (
                  <span className="pf-menu-email">{session.signed_in_as.email}</span>
                )}
                <span>{session?.company || 'Client'}{session?.code ? ` · ${session.code}` : ''}</span>
              </div>
              {/* The four pages the account app's own header links to. Rendered only when
                  VITE_ACCOUNT_URL is configured — a menu item pointing at "undefined/my-profile" is
                  worse than one that is absent. */}
              {ACCOUNT_LINKS.map((item) => accountUrl(item.path) && (
                <a key={item.path} role="menuitem" className="pf-menu-item"
                   href={accountUrl(item.path)}>
                  {item.label}
                </a>
              ))}
              <button role="menuitem" className="pf-menu-item" onClick={onSignOut}>
                <SignOutIcon />Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      )}

      {/* ── the page ─────────────────────────────────────────────────────── */}
      <main className="pf-main">
        <div className="pf-page-head">
          <div className="pf-page-head-row">
            <div>
              <h1>{title}</h1>
              {blurb && <p>{blurb}</p>}
            </div>
            {actions}
          </div>
        </div>
        {children}
      </main>
    </div>
  )
}
